import { ReactElement, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Badge } from "@chakra-ui/react";
import { useVariantTunnel } from "./hooks/useVariantTunnel";
import { Calibration } from "./pages/calibration";
import { SelectRecType } from "./pages/engines/[engineType]/select-rectype";
import { ProtectedRecording } from "./pages/engines/[engineType]/recording";
import { Result } from "./pages/engines/[engineType]/results/[fileId]";
import { AnalysisFailed } from "./pages/engines/[engineType]/results/error";
import { QueryLoginResult } from "./pages/query-login-result";
import { NotFound } from "./pages/404";
import { useTranslation } from "react-i18next";
import { Engines } from "./pages/engines/";
import { EngineType } from "./pages/engines/[engineType]";
import { Login } from "./pages/login";
import { QueryLogin } from "./pages/query-login";
import { useAtom } from "jotai";
import { accessTokenInfoAtom, employeeInfoAtom } from "./store";
import { useQueryLoginRedirect } from "./hooks/useQueryLoginRedirect";
import dayjs from "dayjs";
import { PasswordReset } from "./pages/password-reset";
import { ResetId } from "./pages/password-reset/[resetId]";
import { AccountUnlocked } from "./pages/account-unlocked";
import { ColorLayout } from "./pages/color-layout";
import { SentryRoutes } from "../sentry";
import * as Sentry from "@sentry/react";
import { checkWafResponse } from "./utils/checkWafResponse";
import { DevImportUsers } from "./pages/dev-import-users";

// ページ遷移時にスクロール位置をリセットする
function ScrollToTop(): ReactElement | null {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

function ProtectedRoutes(): ReactElement {
  const { i18n } = useTranslation();
  const [employeeInfo] = useAtom(employeeInfoAtom);
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  let isValid;
  if (employeeInfo.employee_id) {
    const now = dayjs();
    isValid = tokenInfo && dayjs(tokenInfo.expired_at).isAfter(now);
    Sentry.setUser({ email: employeeInfo.employee_id });
  }

  return isValid ? (
    <Outlet />
  ) : (
    <Navigate to={`/${i18n.resolvedLanguage}/login`} replace />
  );
}

function AppRoot(): ReactElement {
  const { lang } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const variant = useVariantTunnel();

  useEffect(() => {
    // i18nに設定したサポートされている言語を取得
    const supportedLanguages = i18n.options.supportedLngs || [];

    if (lang !== undefined && supportedLanguages.includes(lang)) {
      // パスに設定されているlangがi18n設定と異なる場合はi18nの設定を変更
      if (i18n.resolvedLanguage !== lang) {
        i18n.changeLanguage(lang).then();
      }
    } else {
      // langが未サポートの言語の場合はログイン画面に飛ばす
      navigate(`/${i18n.resolvedLanguage}/login`, { replace: true });
    }
  }, [i18n, lang, navigate]);

  const location = useLocation();

  // WAFでメンテナンス時のアクセス制限(ステータスコード503)がかかっていたらリロードする
  // (WebACLSのカスタムレスポンスが表示される)
  useEffect(() => {
    const checkAndReload = async (): Promise<void> => {
      try {
        const status = await checkWafResponse(location.pathname);
        if (status === 503) {
          window.alert(t("Error.waf.maintenance"));
          window.location.reload();
        }
      } catch (error) {
        console.error("WAF確認エラー:", error);
      }
    };

    checkAndReload();
  }, [location.pathname, t]);

  return (
    <>
      {variant && (
        <Badge
          top={4}
          right={4}
          position="fixed"
          variant="solid"
          colorScheme="red"
          pointerEvents="none"
        >
          Variant: {variant}
        </Badge>
      )}
      <Outlet />
    </>
  );
}

function App(): ReactElement {
  const { i18n } = useTranslation();
  const defaultPath = `/${i18n.resolvedLanguage}/login`;
  const rootRedirect = useQueryLoginRedirect({ defaultPath });

  return (
    <>
      <ScrollToTop />
      <SentryRoutes>
        <Route path="/" element={rootRedirect} />
        <Route path=":lang" element={<AppRoot />}>
          <Route index element={<Navigate replace to={defaultPath} />} />
          {/* クエリログイン */}
          <Route path="query-login" element={<QueryLogin />} />
          <Route path="login" element={<Login />} />
          <Route path="account-unlocked" element={<AccountUnlocked />} />
          <Route path="password-reset">
            <Route index element={<PasswordReset />} />
            <Route path=":resetId" element={<ResetId />} />
          </Route>
          <Route path="calibration" element={<Calibration backButton />} />
          {/* カラー/レイアウト設定画面 */}
          <Route path="setting/color_layout" element={<ColorLayout />} />
          {/* 開発者向け隠しルート - サービスマン専用 */}
          <Route
            path={`sys/users/import/${__BUILD_ID__.substring(0, 7)}`}
            element={<DevImportUsers />}
          />

          <Route element={<ProtectedRoutes />}>
            {/* クエリログイン時の音声録音解析完了画面 */}
            <Route path="query-login-result" element={<QueryLoginResult />} />
            <Route path="engines">
              <Route index element={<Engines />} />
              <Route path=":engineType" element={<EngineType />}>
                <Route index element={<Navigate replace to="/404" />} />
                {/* 録音種別選択画面 */}
                <Route path="select-rectype" element={<SelectRecType />} />
                <Route path="recording" element={<ProtectedRecording />} />
                <Route path="results">
                  <Route index element={<Navigate replace to="/404" />} />
                  <Route path="error" element={<AnalysisFailed />} />
                  <Route path=":fileId" element={<Result />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate replace to="/404" />} />
      </SentryRoutes>
    </>
  );
}

export default App;
